/* import __COLOCATED_TEMPLATE__ from './collapsible-list.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type Guideline from 'embercom/models/ai-agent/guidelines';
import { guidFor } from '@ember/object/internals';
import { htmlToTextContent } from 'embercom/lib/html-unescape';
import { getOwner } from '@ember/application';
import RenderableChart from 'embercom/models/reporting/custom/renderable-chart';
import type Chart from 'embercom/models/reporting/custom/chart';
import type ChartSeries from 'embercom/models/reporting/custom/chart-series';
// @ts-ignore no type declaration available for ember-copy
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type ConversationAttributeDescriptor from 'embercom/objects/inbox/conversation-attribute-descriptor';

export enum Stat {
  USED = 'used',
  RESOLVED = 'resolved',
  ROUTED = 'routed',
}

export interface Args {
  title: string;
  category: string;
  description: string;
  guidelines: Guideline[];
  icon: InterfaceIconName;
  maxGuidelineCount: number;
  chartDefinitionsHash: Record<string, Record<Stat, any>>;
  updateChartDefinitions: (guideline: Guideline) => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class GuidelinesCollapsibleList extends Component<Signature> {
  @service declare appService: any;
  @service declare notificationsService: any;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare permissionsService: $TSFixMe;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;

  @tracked isOpen = true;
  @tracked selectedGuideline?: Guideline;
  @tracked sideDrawerOpen = false;
  @tracked chart?: Chart;
  @tracked selection: string[] = [
    'conversation.id',
    'conversation.first_user_conversation_part_created_at',
    'conversation.fin.resolution_state',
    'conversation.channel_type',
    'conversation.bot_conversation_rating.rating_index',
    'conversation.fin.guidelines_applied.guideline_ids',
  ];
  @tracked previousSelection: string[] = [];
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @tracked selectedStat = '';
  @tracked humanIdToUUID: Record<string, string> = {};

  stats = [Stat.USED, Stat.RESOLVED, Stat.ROUTED];

  get app(): any {
    return this.appService.app;
  }

  get arrowIconClass(): InterfaceIconName {
    return this.isOpen ? 'small-thin-down-arrow' : 'small-thin-right-arrow';
  }

  get maybeEmptyStateClasses(): string {
    return this.args.guidelines.length ? '' : 'filter grayscale opacity-50';
  }

  get headerOpenClassName(): string {
    return 'collapsible-list__header__open net-new-flag';
  }

  get openSectionId(): string | null {
    return this.aiAgentGuidelineService.openGuidelineId;
  }

  get guidelinesForCategory(): Guideline[] {
    return this.args.guidelines
      .filter((guideline) => guideline.category === this.args.category && !guideline.isDeleted)
      .sort((a, b) => {
        // If a is temp (unsaved guideline), it comes first
        if (String(a.id).startsWith('temp-')) {
          return -1;
        }
        if (String(b.id).startsWith('temp-')) {
          return 1;
        }

        // Otherwise sort by ID in descending order (highest number first)
        return Number(b.id) - Number(a.id);
      });
  }

  get guidelineCount(): number {
    return this.args.guidelines.filter((guideline) => !guideline.isDeleted).length;
  }

  get cannotAddNewGuidelines(): boolean {
    return this.guidelineCount >= this.args.maxGuidelineCount || this.hasUnsavedGuidelineInCategory;
  }

  get hasUnsavedGuidelineInCategory(): boolean {
    return this.args.guidelines.some(
      (guideline) => guideline.category === this.args.category && !guideline.hasState,
    );
  }

  @action setChartId(chartId: string, originalId: string) {
    // The originalId serves to map this to the actual guideline
    this.humanIdToUUID[originalId] = chartId;
  }

  @action
  cancelGuideline(guideline: Guideline) {
    guideline.rollbackAttributes();
    this.aiAgentGuidelineService.composerRefreshHooks.forEach((hook) => hook());
  }

  @action
  toggleOpenState(): void {
    this.isOpen = !this.isOpen;
  }

  @action
  onOpenSectionChange(sectionId: string) {
    this.aiAgentGuidelineService.setOpenGuidelineId(sectionId);
  }

  @action
  createNewGuideline(): void {
    let newGuideline = this.store.createRecord('ai-agent/guidelines', {
      id: `temp-${guidFor({})}`,
      prompt: '',
      category: this.args.category,
      title: this.intl.t(
        `ai-agent.guidance.guidelines.categories.${this.args.category}.guideline-title`,
        {
          count: this.guidelinesForCategory.length + 1,
        },
      ),
    });

    this.args.updateChartDefinitions(newGuideline);

    this.isOpen = true;
    this.aiAgentGuidelineService.setOpenGuidelineId(newGuideline.id);
  }

  @action
  openSideDrawer(guideline: Guideline, stat: Stat, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    if (this.permissionsService.currentAdminCan('can_reporting__drillin__access')) {
      let chartDefinition = this.args.chartDefinitionsHash[guideline.id][stat];

      let chartId = this.humanIdToUUID[`${guideline.id}_${stat}`];

      this.selectedGuideline = guideline;
      this.selectedStat = stat;

      this.store.pushPayload({
        'reporting/custom/chart': chartDefinition,
      });
      this.chart = this.store.peekRecord('reporting/custom/chart', chartId);
      this.sideDrawerOpen = true;
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
    }
  }

  @action
  closeSideDrawer() {
    this.selectedGuideline = undefined;
    this.sideDrawerOpen = false;
    this.chart = undefined;
  }

  textToContent(text: string) {
    return htmlToTextContent(text);
  }

  get renderableChart() {
    let report = {
      settings: {},
      timezone: this.appService.app.timezone,
    };
    return new RenderableChart(this.chart, report, getOwner(this));
  }

  get knownValuesEndpointSources() {
    return this.chart?.chartSeries.map((series: ChartSeries) => series.metric.firstSource);
  }

  get isStandaloneApp(): boolean {
    return this.appService.app.canUseStandalone;
  }

  @action
  setColumns(chartSeries: ChartSeries, columns: string[]) {
    this.selection = this.reportingUnderlyingDataService.getOrderedAttributeIds(
      chartSeries.metric,
      columns,
      this.previousSelection,
    );

    if (this.chart?.id) {
      this.reportingUnderlyingDataService.setColumnSelectionInCache(
        this.chart.id,
        chartSeries,
        this.selection,
      );
    }

    this.previousSelection = this.selection;
  }

  get columns() {
    return this.selection;
  }

  get chartSeries() {
    return this.chart?.chartSeries.firstObject;
  }

  get conversationAttributeDescriptors(): ConversationAttributeDescriptor[] {
    // Already loaded in the fin-ai-agent route and the standalone guidance route
    return this.store.peekAll('conversation-attributes/descriptor').toArray();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::CollapsibleList': typeof GuidelinesCollapsibleList;
  }
}
